import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCreatePartner from "@hooks/admin/partners/useCreatePartner";
import useUpdatePartner from "@hooks/admin/partners/useUpdatePartner";
import { Box } from "@material-ui/core";
import { Button } from "@runwayhealth/runway-components-react";
import { deletePartner, searchPartners, searchTransactions } from "@store/admin/adminAction";
import { Partner, PartnerUpdate, RootState, Transaction } from "@types-runway/state";
import { toastError, toastSuccess } from "@utils/utils";

import { getInvoiceIdLink } from "./CaseSearch";
import PartnerForm from "./components/PartnerForm";

const PartnerManager = () => {
  const dispatch = useDispatch();
  const partners = useSelector<RootState, Partner[]>((state) => state.admin.partners);
  const deletingPartnerId = useSelector<RootState, string | null>(
    (state) => state.admin.deletingPartnerId
  );
  const [editablePartnerId, setEditablePartnerId] = useState<string | null>(null);
  const [newPartner, setNewPartner] = useState<Partner | null>(null);
  const transactions = useSelector<RootState, Transaction[]>((state) => state.admin.transactions);

  const { postPartner } = useCreatePartner();
  const { updatePartner } = useUpdatePartner();

  useEffect(() => {
    dispatch(searchPartners({}));
  }, []);

  const processPartner = async (partner: Partner | PartnerUpdate) => {
    let processedPartner = null;
    if (editablePartnerId) {
      // Update Partner
      processedPartner = await updatePartner(editablePartnerId, partner as PartnerUpdate);
    } else {
      processedPartner = await postPartner(partner as Partner);
      setNewPartner(processedPartner);
    }

    if (processedPartner) {
      toastSuccess("Success", "Partner processed");
    } else {
      toastError("Failed", "Partner not processed");
    }
  };

  const getPartnerTransactions = (partnerId: string) => {
    dispatch(searchTransactions(partnerId));
  };

  const removePartner = (partnerId: string) => {
    dispatch(deletePartner(partnerId));
  };

  return (
    <section>
      <h2>Partner Management 👩‍💼</h2>
      <p>Onboard or remove partners</p>
      <div className="noMargin">
        <h3>Search partners</h3>
        <Box>
          {partners.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>
                    <b>Edit</b>
                  </th>
                  <th>Name</th>
                  <th>API Key</th>
                  <th>Transactions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => {
                  return (
                    <tr key={partner.clientKey}>
                      <td>
                        <Button
                          isIconOnly
                          iconName="NotePencil"
                          size="sm"
                          variant="outline"
                          onClick={() => setEditablePartnerId(partner.clientKey ?? null)}
                        />
                      </td>
                      <td>{partner.name}</td>
                      <td>{partner.clientKey}</td>
                      <td
                        className="td-pointer"
                        onClick={() => {
                          getPartnerTransactions(partner.clientKey ?? "");
                        }}
                      >
                        {partner.transactionsCount} 👁️‍🗨️
                      </td>
                      <td>
                        <Button
                          isIconOnly
                          iconName="Trash"
                          size="sm"
                          variant="outline"
                          aria-label={`Delete partner ${partner.clientKey}`}
                          disabled={deletingPartnerId === partner.clientKey}
                          isLoading={deletingPartnerId === partner.clientKey}
                          onClick={() => {
                            removePartner(partner.clientKey ?? "");
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Box>
      </div>
      <PartnerForm partnerId={editablePartnerId} onSubmit={processPartner} />
      <div className="noMargin">
        {newPartner?.clientKey && (
          <div id="partner-credentials">
            <div id="credentials-suggestion-message">
              {" "}
              <i>
                Please save the <b>secret key</b> in a safe place, this key will only be visible
                now, and won't be accessible later.
              </i>
            </div>
            <Box>
              <table>
                <thead>
                  <tr>
                    <th>API Key</th>
                    <th>Secret Key</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{newPartner?.clientKey}</td>
                    <td>{newPartner?.clientSecret}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </div>
        )}
      </div>
      <div className="noMargin">
        <h3>Search transactions</h3>
        <Box>
          {transactions.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User Email</th>
                  <th>Invoice</th>
                  <th>Partner ID</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => {
                  return (
                    <tr key={transaction.id}>
                      <td>{transaction.id}</td>
                      <td>{transaction.userEmail}</td>
                      <td>{getInvoiceIdLink(transaction.invoiceId || "❌")}</td>
                      <td>{transaction.PartnerClientKey}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Box>
      </div>
    </section>
  );
};

export default PartnerManager;
